import { getApiBaseUrl } from '../../utils/apiBase';
import { transformFilterItems } from '../../utils/transformFilterItems';
// Using ISO8691 Format
export function getTimeObj() {
  const today = new Date();
  const timeObj = {
    YYYY: today.getUTCFullYear().toString(), // Year (4 digit)
    yyyy: today.getUTCFullYear().toString(), // Year (4 digit)

    YY: today.getUTCFullYear().toString().slice(2), // Year (2 digit)
    yy: today.getUTCFullYear().toString().slice(2), // Year (2 digit)

    MM: ('0' + (today.getUTCMonth() + 1).toString()).slice(-2), // Month

    DD: ('0' + today.getUTCDate().toString()).slice(-2), // Date
    dd: ('0' + today.getUTCDate().toString()).slice(-2), // Date

    HH: ('0' + today.getUTCHours().toString()).slice(-2), // Hour
    hh: ('0' + today.getUTCHours().toString()).slice(-2), // Hour

    mm: ('0' + today.getUTCMinutes().toString()).slice(-2), // Minute

    SS: ('0' + today.getUTCSeconds().toString()).slice(-2), // Seconds
    ss: ('0' + today.getUTCSeconds().toString()).slice(-2), // Seconds
  };
  return timeObj;
}

export function configureFileName(dateValues, exportReportName: string | null): string | null {
  if (!exportReportName) return null;
  const templateString = exportReportName;
  const regex = /{(.+?)}/gm;
  const splitString = templateString.split(regex);

  const replaceSplitString = splitString.map((string) => {
    if (string in dateValues) return dateValues[string];
    else return string;
  });

  return replaceSplitString.join('');
}

function calcColumns(apiRef) {
  const visibleColsObj = apiRef.current.getVisibleColumns();
  return visibleColsObj
    .filter((column) => column.attrAvailableInGrid)
    .map((column) => ({
      attrDisplayNameen: column.attrDisplayNameen,
      attrDisplayNamede: column.attrDisplayNamede,
      attrInternalName: column.attrInternalName,
      field: column.field,
      type: column.type,
    }));
}

async function handleExportManualMoveIt(record) {
  const apiBase = getApiBaseUrl();
  try {
    const response = await fetch(`${apiBase}/api/manualReport`, {
      method: 'POST',
      body: JSON.stringify(record),
    });
    if (!response.ok) throw new Error('exportFunctions.ts Response Not ok');
    return {
      status: 200,
      message: `Sent ${record.report_name}.${record.file_type} to MoveIT `,
    };
  } catch {
    throw new Error('Error Caught in exportFunctions.ts');
  }
}

export function getString(value) {
  return value ? `"${value}"` : '';
}

const OPERATORS_WITHOUT_VALUE = ['isEmpty', 'isNotEmpty'];

function isEmptyFilterValue(value: unknown) {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  );
}

export function sanitizeFilterModelForExport(filterModel) {
  const items = filterModel?.items ?? [];
  const sanitizedItems = items.filter((item) => {
    if (!item) {
      return false;
    }

    if (OPERATORS_WITHOUT_VALUE.includes(item.operator)) {
      return true;
    }

    return !isEmptyFilterValue(item.value);
  });

  return {
    ...filterModel,
    items: sanitizedItems,
  };
}

export default Object.seal({
  async handleExport({
    apiRef,
    config,
    language,
    allDropdowns,
    configSchema,
    fetchReport,
    fileType,
    allAccessibleColumns,
    AdditionalReportFilterModel,
  }) {
    const { dataSchema, dataSource, dataSourceUniqueIdentifier } = config;
    const columns = calcColumns(apiRef);
    const today = new Date();
    const date = today.toJSON().slice(0, 10);
    const formattedDate = date.slice(0, 4) + date.slice(5, 7) + date.slice(8, 10);
    const fileName = `${
      config?.sheetName[0] + '_' + config?.tableName[0] + '_' + 'Data_Table' + '_' + formattedDate
    }.${fileType}`;

    const baseFilterModel = apiRef?.current?.state?.filter?.filterModel || {
      items: [],
      logicOperator: 'and',
    };
    const additionalItems = AdditionalReportFilterModel?.items ?? [];
    const mergedItems = [...(baseFilterModel.items ?? []), ...additionalItems];

    const completeFilterModel = sanitizeFilterModelForExport({
      ...baseFilterModel,
      items: mergedItems,
    });

    const transformedFilterModel = transformFilterItems(completeFilterModel, allAccessibleColumns);

    const jsonPayload = {
      columns,
      configSchema,
      dataSchema,
      dataSource,
      dataSourceUniqueIdentifier,
      dropdowns: allDropdowns,
      fileType,
      filterModel: transformedFilterModel,
      language,
      sortModel: apiRef?.current?.getSortModel(),
    };
    const url = `/api/reports/download-screen-data`;
    const options = {
      body: JSON.stringify(jsonPayload),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    };
    fetchReport({ fileName, url, options });
  },

  handleCustomExport({
    exportType,
    fileNameObj,
    language,
    record,
    fetchReport,
    from = undefined,
    to = undefined,
  }) {
    const reportId = record.mbdReportConfig.id;
    const time = getTimeObj();
    const fileName = configureFileName(
      { ...time },
      fileNameObj.exportReportName || `{YYYY}{MM}{DD}_${fileNameObj.reportName}`,
    );
    const extMap = {
      'use case2 for excel': 'xlsx',
      'use case2 for csv': 'csv',
      'use case2 for xml': 'xml',
    };
    const fileExt = extMap[exportType] || 'csv';

    const searchParams = new URLSearchParams({
      fileExt,
      language,
      reportId,
    });
    if (from && to) {
      searchParams.set('from', from);
      searchParams.set('to', to);
    }

    // const url = `/api/reports/${reportId}?${searchParams}`;
    const url = `/api/reports/download-report?${searchParams}`;
    const options = {};
    fetchReport({ fileName: `${fileName}.${fileExt}`, url, options });
  },

  async sendToMoveIt({ record, configSchema }) {
    const apiBase = getApiBaseUrl();
    // conforming to input of secondary-lambda
    const reportId = record.mbdReportConfig.id;
    const response = await fetch(
      `${apiBase}/api/fetchManualReportByName?reportId=${encodeURIComponent(reportId)}&configSchema=${encodeURIComponent(configSchema)}`,
    );
    const data = await response.json();
    const promiseArrayFileType = data.map((record) => handleExportManualMoveIt(record));
    return await Promise.all(promiseArrayFileType);
  },
});
